<template>
  <div class="landing-page">
    <!-- Rotating Earth Background -->
    <RotatingEarthBackground />

    <!-- Sticky Header -->
    <header class="sticky-header" :class="{ 'scrolled': isScrolled }">
      <div class="header-container">
        <div class="header-logo">
          <span class="logo-icon">🌍</span>
          <span class="logo-text">Captain Leo's Maps</span>
        </div>

        <nav class="header-nav">
          <a
            v-for="section in sections"
            :key="section.id"
            :href="`#${section.id}`"
            class="nav-link"
            :class="{ 'active': activeSection === section.id }"
            @click.prevent="scrollToSection(section.id)"
          >
            {{ section.name }}
          </a>
        </nav>

        <router-link to="/app" class="launch-app-btn">
          <i class="fas fa-rocket"></i>
          <span>Launch App</span>
          <div class="btn-shine"></div>
        </router-link>

        <!-- Mobile Menu Toggle -->
        <button class="mobile-menu-toggle" @click="toggleMobileMenu">
          <i class="fas" :class="mobileMenuOpen ? 'fa-times' : 'fa-bars'"></i>
        </button>
      </div>

      <!-- Mobile Menu -->
      <div class="mobile-menu" :class="{ 'open': mobileMenuOpen }">
        <a
          v-for="section in sections"
          :key="section.id"
          :href="`#${section.id}`"
          class="mobile-nav-link"
          :class="{ 'active': activeSection === section.id }"
          @click.prevent="scrollToSection(section.id); closeMobileMenu()"
        >
          {{ section.name }}
        </a>
        <router-link to="/app" class="mobile-launch-btn" @click="closeMobileMenu">
          <i class="fas fa-rocket"></i>
          Launch App
        </router-link>
      </div>
    </header>

    <!-- Hero Section -->
    <section id="hero" class="hero-section">
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">
            Turn Geography Into a Magical Adventure
          </h1>
          <p class="hero-subtitle">
            Explore volcanoes, jungles, pyramids, castles, and hidden wonders from around the world. Learn geography through exploration, discovery, and adventure.
          </p>
          <div class="hero-buttons">
            <router-link to="/app" class="btn-primary">
              <i class="fas fa-compass"></i>
              Start Exploring
            </router-link>
          </div>
        </div>
        <div class="hero-image">
          <div class="captain-leo-container">
            <img
              src="@/assets/leo-binoculars.png"
              alt="Captain Leo with binoculars"
              class="captain-leo-img"
            />
          </div>
        </div>
      </div>
      <div class="scroll-indicator" @click="scrollToFeatures">
        <span>Discover More</span>
        <i class="fas fa-chevron-down"></i>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="container">
        <h2 class="section-title">What Young Explorers Can Do</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon-placeholder">
              <!-- TODO: Replace with illustrated compass icon -->
              <i class="fas fa-compass"></i>
            </div>
            <h3>Discover Hidden Places</h3>
            <p>Click anywhere on interactive maps to uncover fascinating facts about places you've never heard of.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon-placeholder">
              <!-- TODO: Replace with illustrated puzzle/challenge icon -->
              <i class="fas fa-puzzle-piece"></i>
            </div>
            <h3>Solve Geography Challenges</h3>
            <p>Test your knowledge with flag quizzes, clue-based mysteries, and place challenges across every continent.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon-placeholder">
              <!-- TODO: Replace with illustrated badge/medal icon -->
              <i class="fas fa-medal"></i>
            </div>
            <h3>Earn Explorer Badges</h3>
            <p>Climb the leaderboard, earn Diamond medals, and become a Master Explorer among your friends.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon-placeholder">
              <!-- TODO: Replace with illustrated Captain Leo icon -->
              <i class="fas fa-hat-wizard"></i>
            </div>
            <h3>Travel With Captain Leo</h3>
            <p>Your explorer guide shares stories about cultures, landmarks, wildlife, and famous people from every corner of the globe.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Interactive Map Preview Section -->
    <section id="app-preview" class="app-preview-section">
      <div class="container">
        <h2 class="section-title">See the Adventure in Action</h2>
        <p class="section-subtitle">Real views from inside Captain Leo's Maps</p>

        <div class="device-mockups">
          <div class="device-frame device-desktop">
            <div class="device-bezel">
              <div class="device-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
            <div class="device-screen">
              <!-- TODO: Replace with real screenshot of map exploration view -->
              <div class="screenshot-placeholder-img">
                <i class="fas fa-map-marked-alt"></i>
                <p>Map Exploration View</p>
                <span class="placeholder-hint">screenshot-map-exploration.png</span>
              </div>
            </div>
            <p class="device-caption">Click anywhere on the globe to discover a new place</p>
          </div>

          <div class="device-pair">
            <div class="device-frame device-tablet">
              <div class="device-bezel device-bezel-thin"></div>
              <div class="device-screen">
                <!-- TODO: Replace with real screenshot of quiz UI -->
                <div class="screenshot-placeholder-img">
                  <i class="fas fa-question-circle"></i>
                  <p>Geography Quizzes</p>
                  <span class="placeholder-hint">screenshot-quiz.png</span>
                </div>
              </div>
              <p class="device-caption">Solve geography challenges to earn points</p>
            </div>
            <div class="device-frame device-mobile">
              <div class="device-bezel device-bezel-thin"></div>
              <div class="device-screen">
                <!-- TODO: Replace with real screenshot of destination info -->
                <div class="screenshot-placeholder-img">
                  <i class="fas fa-info-circle"></i>
                  <p>Place Details</p>
                  <span class="placeholder-hint">screenshot-place-info.png</span>
                </div>
              </div>
              <p class="device-caption">Learn about cultures and landmarks</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Explore Destinations Section (merged destinations + attractions) -->
    <section id="destinations" class="destinations-section">
      <div class="container">
        <h2 class="section-title">Explore the World</h2>
        <p class="section-subtitle">Discover incredible places and landmarks from every continent</p>

        <!-- Places -->
        <div v-if="loadingPlaces && loadingAttractions" class="loading-container">
          <div class="spinner"></div>
          <p>Loading expeditions...</p>
        </div>

        <div v-else>
          <div class="places-grid" v-if="popularPlaces.length > 0">
            <div v-for="place in popularPlaces" :key="place.slug" class="place-card" @click="explorePlace(place)">
              <div class="place-image">
                <img :src="place.image" :alt="place.name" @error="handleImageError" />
                <div class="place-overlay">
                  <i class="fas fa-compass"></i>
                  <span>Explore</span>
                </div>
                <div class="country-flag">{{ getCountryFlag(place.countryCode) }}</div>
              </div>
              <div class="place-info">
                <h3>{{ place.name }}</h3>
                <p class="place-location">
                  <i class="fas fa-map-marker-alt"></i>
                  <span v-if="place.state">{{ place.state }}, </span>{{ place.country }}
                </p>
              </div>
            </div>
          </div>

          <!-- Landmarks row -->
          <div v-if="landmarks.length > 0" class="landmarks-row">
            <h3 class="subsection-title">Notable Landmarks</h3>
            <div class="landmarks-grid">
              <div v-for="landmark in landmarks.slice(0, 4)" :key="landmark._id" class="landmark-card" @click="exploreLandmark(landmark)">
                <div class="landmark-image">
                  <img :src="landmark.image" :alt="landmark.title" @error="handleImageError" />
                  <div class="landmark-overlay">
                    <i class="fas fa-compass"></i>
                  </div>
                </div>
                <div class="landmark-info">
                  <h4>{{ landmark.title }}</h4>
                  <span class="landmark-location">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ landmark.placeName }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section id="benefits" class="benefits-section">
      <div class="container">
        <h2 class="section-title">Train Your Explorer Brain</h2>
        <div class="benefits-grid">
          <div class="benefit-item">
            <div class="benefit-icon-placeholder">
              <!-- TODO: Replace with illustrated compass icon -->
              <i class="fas fa-compass"></i>
            </div>
            <h3>Become a Master Explorer</h3>
            <p>Build spatial awareness and navigate the world like a pro</p>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon-placeholder">
              <!-- TODO: Replace with illustrated globe-with-route icon -->
              <i class="fas fa-globe-americas"></i>
            </div>
            <h3>Learn About Cultures</h3>
            <p>Discover traditions, foods, and celebrations from every continent</p>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon-placeholder">
              <!-- TODO: Replace with illustrated telescope icon -->
              <i class="fas fa-binoculars"></i>
            </div>
            <h3>Sharpen Your Mind</h3>
            <p>Solve clue-based mysteries and flag challenges that make you think</p>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon-placeholder">
              <!-- TODO: Replace with illustrated treasure chest icon -->
              <i class="fas fa-gem"></i>
            </div>
            <h3>Build Curiosity Through Adventure</h3>
            <p>Every click on the map leads to a new discovery waiting to be made</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Blog Preview Section -->
    <section class="blog-section">
      <div class="container">
        <h2 class="section-title">Latest Adventures</h2>

        <div v-if="loadingArticles" class="loading-container">
          <div class="spinner"></div>
          <p>Loading latest adventures...</p>
        </div>

        <div v-else-if="articles.length === 0" class="no-places">
          <p>No articles available at the moment.</p>
        </div>

        <div v-else class="blog-grid">
          <article v-for="article in articles" :key="article.slug" class="blog-card" @click="readArticle(article)">
            <div class="blog-image">
              <img :src="article.image" :alt="article.title" @error="handleImageError" />
              <div class="blog-category" v-if="article.categorySlug">{{ formatCategory(article.categorySlug) }}</div>
            </div>
            <div class="blog-content">
              <h3>{{ article.title }}</h3>
              <p class="blog-excerpt">{{ article.excerpt }}</p>
              <div class="blog-meta">
                <span class="blog-date">
                  <i class="fas fa-calendar"></i>
                  {{ formatDate(article.date) }}
                </span>
              </div>
              <button class="read-more-btn">
                Read More <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Global Leaderboard Section -->
    <section id="leaderboard" class="leaderboard-section">
      <div class="container">
        <h2 class="section-title">Top Explorers</h2>
        <p class="section-subtitle">Meet the world's greatest geography adventurers!</p>

        <div v-if="loadingLeaderboard" class="loading-container">
          <div class="spinner"></div>
          <p>Loading top explorers...</p>
        </div>

        <div v-else-if="leaderboardError" class="error-message">
          <p>{{ leaderboardError }}</p>
        </div>

        <div v-else class="leaderboard-container">
          <!-- Top 3 Podium -->
          <div class="podium" v-if="topThree.length > 0">
            <div v-for="(entry, index) in topThree" :key="entry.user_id" class="podium-place" :class="`place-${index + 1}`">
              <div class="podium-rank">
                <!-- TODO: Replace with illustrated explorer trophy icons -->
                <div class="rank-trophy" :class="`trophy-${index + 1}`">
                  <i class="fas fa-trophy"></i>
                </div>
              </div>
              <div class="podium-user">
                <div class="user-avatar">
                  <i class="fas fa-user-astronaut"></i>
                </div>
                <span class="explorer-title">{{ ['Atlas Legend', 'Pathfinder', 'Trailblazer'][index] }}</span>
                <router-link :to="`/profile/${entry.name || entry.username}`" class="user-name-link">
                  {{ entry.name || entry.username || 'Explorer' }}
                </router-link>
                <div class="user-score">
                  <i class="fas fa-star"></i>
                  {{ entry.total_score || 0 }}
                </div>
              </div>
            </div>
          </div>

          <!-- Leaderboard Table (4-10) -->
          <div class="leaderboard-table-wrapper" v-if="remainingEntries.length > 0">
            <div class="leaderboard-table">
              <div class="table-row" v-for="entry in remainingEntries" :key="entry.user_id">
                <div class="rank-cell">
                  <span class="rank-badge">{{ entry.rank }}</span>
                </div>
                <div class="user-cell">
                  <div class="user-info">
                    <i class="fas fa-user-circle user-icon"></i>
                    <router-link :to="`/profile/${entry.name || entry.username}`" class="user-name">
                      {{ entry.name || entry.username || 'Explorer' }}
                    </router-link>
                  </div>
                </div>
                <div class="score-cell">
                  <span class="score-value">
                    <i class="fas fa-star"></i>
                    {{ entry.total_score || 0 }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- View Full Leaderboard Button -->
          <div class="leaderboard-cta">
            <router-link to="/leaderboards" class="btn-leaderboard">
              <i class="fas fa-trophy"></i>
              View Full Leaderboard
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Subscription Section -->
    <section class="subscription-section">
      <div class="container">
        <div class="subscription-content">
          <h2>Unlock More Adventures</h2>
          <p>Go beyond the basics and explore the world without limits</p>
          <div class="subscription-plans">
            <div class="plan-card">
              <h3>Junior Explorer</h3>
              <div class="plan-price">Free</div>
              <ul class="plan-features">
                <li><i class="fas fa-compass"></i> Explore the interactive map</li>
                <li><i class="fas fa-compass"></i> 10 quiz challenges per day</li>
                <li><i class="fas fa-compass"></i> Popular destinations</li>
              </ul>
              <button class="plan-btn current-plan">Current Plan</button>
            </div>
            <div class="plan-card premium">
              <div class="plan-badge">World Explorer Pass</div>
              <h3>Captain Leo Pro</h3>
              <div class="plan-price">$1.99<span>/month</span></div>
              <ul class="plan-features">
                <li><i class="fas fa-star"></i> Unlock secret expeditions</li>
                <li><i class="fas fa-star"></i> Access rare landmarks</li>
                <li><i class="fas fa-star"></i> Join weekly challenges</li>
                <li><i class="fas fa-star"></i> Earn exclusive explorer badges</li>
                <li><i class="fas fa-star"></i> Ad-free adventure</li>
              </ul>
              <button @click="subscribe" class="plan-btn premium-btn">
                <i class="fas fa-compass"></i>
                Begin Your Expedition
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <img
            src="@/assets/leo-map.png"
            alt="Captain Leo ready to explore"
            class="cta-leo-img"
          />
          <h2>The World Is Waiting to Be Explored</h2>
          <p>Join thousands of young explorers discovering amazing places every day.</p>
          <router-link to="/app" class="btn-cta">
            <i class="fas fa-compass"></i>
            Begin Your Adventure
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import RotatingEarthBackground from '@/components/RotatingEarthBackground.vue'

export default {
  name: 'LandingPage',
  components: {
    RotatingEarthBackground
  },
  data() {
    return {
      isScrolled: false,
      activeSection: 'hero',
      mobileMenuOpen: false,
      sections: [
        { id: 'hero', name: 'Home' },
        { id: 'features', name: 'Explore' },
        { id: 'app-preview', name: 'Preview' },
        { id: 'destinations', name: 'Destinations' },
        { id: 'benefits', name: 'Benefits' },
        { id: 'leaderboard', name: 'Leaderboard' }
      ],
      popularPlaces: [],
      loadingPlaces: true,
      landmarks: [],
      loadingAttractions: true,
      articles: [],
      loadingArticles: true,
      leaderboardEntries: [],
      loadingLeaderboard: true,
      leaderboardError: null
    }
  },
  computed: {
    topThree() {
      return this.leaderboardEntries.slice(0, 3);
    },
    remainingEntries() {
      return this.leaderboardEntries.slice(3, 10);
    }
  },
  methods: {
    async fetchPopularPlaces() {
      try {
        this.loadingPlaces = true;
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/places/popular?limit=6`);
        const data = await response.json();

        if (data.success && data.data) {
          // Use the places directly from backend (already filtered and limited to 6)
          this.popularPlaces = data.data;
        }
      } catch (error) {
        console.error('Error fetching popular places:', error);
        this.popularPlaces = [];
      } finally {
        this.loadingPlaces = false;
      }
    },
    async fetchLandmarks() {
      try {
        this.loadingAttractions = true;
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/items/landmarks/random?limit=6`);
        const data = await response.json();

        if (data.success && data.data) {
          this.landmarks = data.data;
        }
      } catch (error) {
        console.error('Error fetching landmarks:', error);
        this.landmarks = [];
      } finally {
        this.loadingAttractions = false;
      }
    },
    getCountryFlag(countryCode) {
      if (!countryCode || countryCode.length !== 2) return '🌍';

      // Convert country code to flag emoji
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt());
      return String.fromCodePoint(...codePoints);
    },
    handleImageError(event) {
      // Fallback image if image fails to load
      event.target.src = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop';
    },
    scrollToFeatures() {
      document.getElementById('features').scrollIntoView({ behavior: 'smooth' });
    },
    explorePlace(place) {
      // Navigate to location page with place slug
      if (place.slug) {
        this.$router.push(`/location/${place.slug}`);
      } else {
        // Fallback to search if no slug
        this.$router.push(`/app?search=${encodeURIComponent(place.name)}`);
      }
    },
    learnMore(attraction) {
      // Navigate to app with attraction search
      this.$router.push(`/app?search=${encodeURIComponent(attraction.name)}`);
    },
    exploreLandmark(landmark) {
      // Navigate to app with the item's place slug
      if (landmark.placeSlug) {
        this.$router.push(`/location/${landmark.placeSlug}`);
      } else {
        // Fallback to search if no slug
        this.$router.push(`/app?search=${encodeURIComponent(landmark.title)}`);
      }
    },
    async fetchArticles() {
      try {
        this.loadingArticles = true;
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/articles/random?limit=3`);
        const data = await response.json();

        if (data.success && data.data) {
          this.articles = data.data;
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        this.articles = [];
      } finally {
        this.loadingArticles = false;
      }
    },
    readArticle(article) {
      // Open article in new window using the slug
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
      window.open(`${apiUrl}/article/${article.slug}`, '_blank');
    },
    async fetchLeaderboard() {
      try {
        this.loadingLeaderboard = true;
        this.leaderboardError = null;
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/leaderboards/global?limit=10`);
        const data = await response.json();

        if (data.success && data.data && Array.isArray(data.data)) {
          this.leaderboardEntries = data.data;
        } else {
          this.leaderboardError = 'Unable to load leaderboard data';
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        this.leaderboardError = 'Failed to load top explorers';
        this.leaderboardEntries = [];
      } finally {
        this.loadingLeaderboard = false;
      }
    },
    formatCategory(categorySlug) {
      // Format category slug to readable text
      return categorySlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    formatDate(date) {
      // Format date to readable format
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    subscribe() {
      // Handle subscription logic
      console.log('Subscribe to premium plan');
      // This will be implemented later with actual payment integration
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    handleScroll() {
      // Update scrolled state
      this.isScrolled = window.scrollY > 50;

      // Update active section
      const scrollPosition = window.scrollY + 150;

      for (let i = this.sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(this.sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          this.activeSection = this.sections[i].id;
          break;
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll(); // Initial call
    this.fetchPopularPlaces(); // Fetch popular places from backend
    this.fetchLandmarks(); // Fetch random landmarks from backend
    this.fetchArticles(); // Fetch random articles from backend
    this.fetchLeaderboard(); // Fetch top 10 global leaderboard
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style scoped>
/* Color Variables - Direct values since :root doesn't work well with scoped styles */

.landing-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  color: #1e293b;
}

/* Sticky Header */
.sticky-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.sticky-header.scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.header-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  cursor: pointer;
}

.logo-icon {
  font-size: 1.75rem;
  animation: gentle-spin 20s linear infinite;
}

@keyframes gentle-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.logo-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-link {
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
  position: relative;
}

.nav-link:hover {
  color: #667eea;
  background: rgba(102, 126, 234, 0.08);
}

.nav-link.active {
  color: #667eea;
  background: rgba(102, 126, 234, 0.12);
  font-weight: 600;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 16px;
  right: 16px;
  height: 2px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.launch-app-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.launch-app-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(102, 126, 234, 0.5);
}

.launch-app-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.launch-app-btn:hover::before {
  left: 100%;
}

.btn-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shine 3s infinite;
}

@keyframes shine {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

.mobile-menu-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #1e293b;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s ease;
}

.mobile-menu-toggle:hover {
  color: #667eea;
}

.mobile-menu {
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: white;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  transform: translateY(-100%);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  max-height: calc(100vh - 70px);
  overflow-y: auto;
}

.mobile-menu.open {
  transform: translateY(0);
  opacity: 1;
  visibility: visible;
}

.mobile-nav-link {
  display: block;
  padding: 15px 20px;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.mobile-nav-link:hover {
  background: rgba(102, 126, 234, 0.08);
  color: #667eea;
}

.mobile-nav-link.active {
  background: rgba(102, 126, 234, 0.12);
  color: #667eea;
  font-weight: 600;
}

.mobile-launch-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  margin-top: 15px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

/* Hero Section */
.hero-section {
  min-height: 100vh;
  background: transparent;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.6) 0%,
    rgba(118, 75, 162, 0.6) 100%
  );
  z-index: 0;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  margin: 0 0 20px 0;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 40px 0;
  line-height: 1.6;
}

.hero-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-primary, .btn-secondary, .btn-outline, .btn-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-primary:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

/* Hero Animation */
.captain-leo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 40px;
  animation: float 3s ease-in-out infinite;
}

.captain-leo-img {
  max-width: 100%;
  height: auto;
  max-height: 400px;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
  animation: gentle-bounce 4s ease-in-out infinite;
}

@keyframes gentle-bounce {
  0%, 100% { transform: translateY(0px) scale(1); }
  25% { transform: translateY(-10px) scale(1.02); }
  75% { transform: translateY(-5px) scale(0.98); }
}

/* Scroll Indicator */
.scroll-indicator {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: color 0.3s ease;
  animation: scroll-bounce 2s ease-in-out infinite;
}

.scroll-indicator:hover {
  color: white;
}

.scroll-indicator span {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.scroll-indicator i {
  font-size: 1.25rem;
}

@keyframes scroll-bounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(8px); }
}

/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Section Styling */
section {
  padding: 80px 0;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 60px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Features Section */
.features-section {
  background: #f8fafc;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
}

.feature-card {
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.feature-icon-placeholder {
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 2px dashed rgba(102, 126, 234, 0.3);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon-placeholder {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
}

.feature-icon-placeholder i {
  font-size: 36px;
  color: #667eea;
  transition: color 0.3s ease;
}

.feature-card:hover .feature-icon-placeholder i {
  color: white;
}

.feature-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 15px 0;
}

.feature-card p {
  color: #64748b;
  margin: 0;
}

/* App Preview Section */
.app-preview-section {
  background: #f8fafc;
}

.section-subtitle {
  text-align: center;
  font-size: 1.125rem;
  color: #64748b;
  margin: -40px 0 50px 0;
}

.device-mockups {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
}

.device-frame {
  background: #1e293b;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.device-bezel {
  background: #334155;
  padding: 10px 16px;
  display: flex;
  align-items: center;
}

.device-bezel-thin {
  padding: 8px 16px;
}

.device-dots {
  display: flex;
  gap: 6px;
}

.device-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #475569;
}

.device-dots span:first-child { background: #ef4444; }
.device-dots span:nth-child(2) { background: #eab308; }
.device-dots span:last-child { background: #22c55e; }

.device-screen {
  background: #0f172a;
  min-height: 200px;
}

.device-desktop {
  width: 100%;
  max-width: 800px;
}

.device-desktop .device-screen {
  aspect-ratio: 16 / 9;
}

.device-pair {
  display: flex;
  gap: 30px;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
}

.device-tablet {
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
}

.device-tablet .device-screen {
  aspect-ratio: 4 / 3;
}

.device-mobile {
  width: 100%;
  max-width: 220px;
  border-radius: 24px;
}

.device-mobile .device-screen {
  aspect-ratio: 9 / 16;
}

.screenshot-placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  gap: 12px;
  padding: 20px;
}

.screenshot-placeholder-img i {
  font-size: 3rem;
  opacity: 0.5;
}

.screenshot-placeholder-img p {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.screenshot-placeholder-img .placeholder-hint {
  font-size: 0.75rem;
  opacity: 0.4;
  font-family: monospace;
}

.device-caption {
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
  margin: 12px 0 0;
  font-weight: 500;
}

/* Popular Places Section */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 4px solid rgba(102, 126, 234, 0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-container p {
  color: #64748b;
  font-size: 1.1rem;
}

.no-places {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.places-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.place-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
}

.place-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.15);
}

.place-image {
  position: relative;
  overflow: hidden;
  height: 220px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.place-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.place-card:hover .place-image img {
  transform: scale(1.1);
}

.place-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.place-card:hover .place-overlay {
  opacity: 1;
}

.country-flag {
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 2.5rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  z-index: 2;
}

.place-info {
  padding: 25px;
}

.place-info h3 {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: #1e293b;
}

.place-location {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px 0;
  color: #667eea;
  font-weight: 600;
  font-size: 0.95rem;
}

.place-location i {
  font-size: 0.9rem;
}

.place-description {
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
}

/* Destinations Section */
.destinations-section {
  background: white;
}

.landmarks-row {
  margin-top: 50px;
}

.subsection-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 25px 0;
  text-align: center;
}

.landmarks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.landmark-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8fafc;
  border-radius: 14px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.landmark-card:hover {
  background: white;
  border-color: rgba(102, 126, 234, 0.2);
  box-shadow: 0 8px 20px -5px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.landmark-image {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.landmark-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.landmark-card:hover .landmark-image img {
  transform: scale(1.1);
}

.landmark-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.landmark-card:hover .landmark-overlay {
  opacity: 1;
}

.landmark-info h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1e293b;
}

.landmark-location {
  font-size: 0.85rem;
  color: #667eea;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.landmark-location i {
  font-size: 0.75rem;
}

/* Benefits Section */
.benefits-section {
  background: #f8fafc;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
}

.benefit-item {
  text-align: center;
  padding: 30px 20px;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.benefit-item:hover {
  background: white;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
}

.benefit-icon-placeholder {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 2px dashed rgba(102, 126, 234, 0.3);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
}

.benefit-item:hover .benefit-icon-placeholder {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
}

.benefit-icon-placeholder i {
  font-size: 2rem;
  color: #667eea;
  transition: color 0.3s ease;
}

.benefit-item:hover .benefit-icon-placeholder i {
  color: white;
}

.benefit-item h3 {
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #1e293b;
}

.benefit-item p {
  color: #64748b;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Blog Section */
.blog-section {
  background: #f8fafc;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.blog-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
}

.blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.15);
}

.blog-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-category {
  position: absolute;
  top: 15px;
  left: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.blog-content {
  padding: 25px;
}

.blog-content h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 15px 0;
}

.blog-excerpt {
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.6;
}

.blog-meta {
  display: flex;
  gap: 20px;
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #64748b;
}

.read-more-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.read-more-btn:hover {
  transform: translateY(-1px);
}

.blog-cta {
  text-align: center;
  margin-top: 50px;
}

.btn-outline {
  background: transparent;
  color: #1e293b;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Subscription Section */
.subscription-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.subscription-content {
  text-align: center;
}

.subscription-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 20px 0;
}

.subscription-content p {
  font-size: 1.25rem;
  margin: 0 0 50px 0;
  opacity: 0.9;
}

.subscription-plans {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 800px;
  margin: 0 auto;
}

.plan-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 40px 30px;
  position: relative;
  backdrop-filter: blur(10px);
}

.plan-card.premium {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.plan-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.plan-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 15px 0;
}

.plan-price {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 30px 0;
}

.plan-price span {
  font-size: 1rem;
  font-weight: 400;
  opacity: 0.7;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px 0;
}

.plan-features i {
  color: rgba(255, 255, 255, 0.7);
}

.plan-btn {
  width: 100%;
  padding: 14px 20px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.current-plan {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.premium-btn {
  background: white;
  color: #1e293b;
}

.premium-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%);
  text-align: center;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-leo-img {
  max-width: 180px;
  height: auto;
  margin-bottom: 30px;
  filter: drop-shadow(0 10px 30px rgba(102, 126, 234, 0.4));
  animation: gentle-bounce 4s ease-in-out infinite;
}

.cta-section h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: white;
}

.cta-section p {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 40px 0;
}

.btn-cta {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 1.25rem;
  padding: 18px 40px;
  border-radius: 50px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
}

.btn-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(102, 126, 234, 0.6);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-nav {
    display: none;
  }

  .launch-app-btn {
    display: none;
  }

  .mobile-menu-toggle {
    display: block;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 0 20px;
  }

  .header-logo {
    font-size: 1.1rem;
  }

  .logo-icon {
    font-size: 1.5rem;
  }

  .hero-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  section {
    padding: 60px 0;
  }

  .subscription-plans {
    grid-template-columns: 1fr;
  }

  .plan-card.premium {
    transform: none;
  }
}

@media (max-width: 480px) {
  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-primary, .btn-secondary {
    width: 100%;
    justify-content: center;
  }
  
  .places-grid, .landmarks-grid, .features-grid {
    grid-template-columns: 1fr;
  }

  .device-pair {
    flex-direction: column;
    align-items: center;
  }

  .device-tablet, .device-mobile {
    max-width: 100%;
  }
}

/* Leaderboard Section */
.leaderboard-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 100px 0;
  color: white;
}

.leaderboard-section .section-title {
  -webkit-text-fill-color: white;
  text-align: center;
  margin-bottom: 10px;
}

.leaderboard-section .section-subtitle {
  text-align: center;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 50px;
}

.leaderboard-container {
  max-width: 900px;
  margin: 0 auto;
}

/* Podium Styles */
.podium {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 40px;
}

.podium-place {
  flex: 1;
  max-width: 200px;
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px 20px 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.podium-place:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.podium-place.place-1 {
  order: 2;
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
}

.podium-place.place-1:hover {
  transform: scale(1.1) translateY(-5px);
}

.podium-place.place-2 {
  order: 1;
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
}

.podium-place.place-3 {
  order: 3;
  background: linear-gradient(135deg, #cd7f32 0%, #d99a6c 100%);
}

.podium-rank {
  margin-bottom: 15px;
}

/* Explorer rank trophies */
.rank-trophy {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.rank-trophy i {
  font-size: 2rem;
}

.trophy-1 {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
}

.trophy-1 i { color: #92400e; }

.trophy-2 {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  box-shadow: 0 4px 15px rgba(192, 192, 192, 0.4);
}

.trophy-2 i { color: #475569; }

.trophy-3 {
  background: linear-gradient(135deg, #cd7f32 0%, #d99a6c 100%);
  box-shadow: 0 4px 15px rgba(205, 127, 50, 0.4);
}

.trophy-3 i { color: #451a03; }

.explorer-title {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #667eea;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.podium-user .user-avatar {
  width: 70px;
  height: 70px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.podium-user .user-avatar i {
  font-size: 3rem;
  color: #667eea;
}

.user-name-link {
  display: block;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1e293b;
  text-decoration: none;
  margin-bottom: 10px;
  transition: color 0.2s ease;
}

.user-name-link:hover {
  color: #667eea;
}

.podium-user .user-score {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
}

.podium-user .user-score i {
  color: #fbbf24;
  font-size: 1rem;
}

/* Leaderboard Table */
.leaderboard-table-wrapper {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  margin-bottom: 30px;
}

.leaderboard-table {
  width: 100%;
}

.table-row {
  display: grid;
  grid-template-columns: 80px 1fr 150px;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s ease;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f8fafc;
}

.rank-cell {
  display: flex;
  justify-content: center;
}

.rank-badge {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}

.user-cell {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-icon {
  font-size: 2rem;
  color: #667eea;
}

.user-name {
  font-weight: 600;
  color: #1e293b;
  text-decoration: none;
  transition: color 0.2s ease;
}

.user-name:hover {
  color: #667eea;
}

.score-cell {
  display: flex;
  justify-content: flex-end;
}

.score-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.score-value i {
  color: #fbbf24;
  font-size: 1rem;
}

/* Leaderboard CTA */
.leaderboard-cta {
  text-align: center;
}

.btn-leaderboard {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 35px;
  background: white;
  color: #667eea;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
}

.btn-leaderboard:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  background: #f8fafc;
}

.btn-leaderboard i {
  font-size: 1.2rem;
}

/* Leaderboard Responsive */
@media (max-width: 768px) {
  .podium {
    flex-direction: column;
    align-items: center;
  }

  .podium-place {
    max-width: 100%;
    width: 100%;
  }

  .podium-place.place-1 {
    order: 1;
    transform: scale(1);
  }

  .podium-place.place-1:hover {
    transform: translateY(-5px);
  }

  .podium-place.place-2 {
    order: 2;
  }

  .podium-place.place-3 {
    order: 3;
  }

  .table-row {
    grid-template-columns: 60px 1fr 100px;
    padding: 15px 20px;
  }

  .rank-badge {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }

  .user-icon {
    font-size: 1.5rem;
  }

  .user-name {
    font-size: 0.95rem;
  }

  .score-value {
    font-size: 1rem;
  }
}
</style>